import { create } from 'zustand';

import { Direction3D, Line3D, LineSegment3D, Point3D, Polygon3D } from '@paragon/geometry-library';
import { circularPairwise, forceArray, notNullish } from '@paragon/utilities';

import { store } from '.';
import { selectItemsByGuid } from './actions/selectActions';

const debugLayoutVisuals = create<{ json: string; debugEnabled: boolean }>(() => ({
  json: 'null',
  debugEnabled: false,
}));

export const useDebugLayoutVisuals = () => processDebugLayoutVisualsJSON(debugLayoutVisuals(({ json }) => json));
export const useShowDebugLayoutVisuals = () => debugLayoutVisuals(({ debugEnabled }) => debugEnabled);

export const setDebugLayoutVisualsJson = (json: string) => debugLayoutVisuals.setState({ json });

const setDebugLayoutVisualsOn = (debugEnabled: boolean) => debugLayoutVisuals.setState({ debugEnabled });
if (process.env.APP_ENV !== 'production')
  (globalThis as unknown as any).setDebugLayoutVisualsOn = setDebugLayoutVisualsOn;

const processDebugLayoutVisualsJSON = (json: string) => {
  // json should be either an array or a single instance of any combination of the following:
  // Polygon(2|3)Ds—have a "vertices" property which is an array of 2- or 3D points
  // LineSegment(2|3)Ds—have a "basePoint" & "endPoint" property which are 2- or 3D points
  // Line2Ds—have a "basePoint" & "direction" property which are 2- or 3D points
  // Line3Ds—have a "pointNearestOrigin" & "direction" property which are 2- or 3D points
  // Point(2|3)Ds—have "x", "y", and possibly "z" properties which are numbers
  // everything should be validated and nothing should throw an error.

  const parsed = (() => {
    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  })();
  if (!parsed) return [];
  const array = forceArray(parsed);
  const objects = [];
  for (const item of array) {
    if (typeof item !== 'object') continue;
    const object = tryParsePoint(item) ?? tryParseSegment(item) ?? tryParseLine(item) ?? tryParsePolygon(item);
    if (object) objects.push(object);
  }
  return objects;
};

/** If item has "x", "y", and optionally "z", all numbers, return the represented point, otherwise null. */
const tryParsePoint = (item: any): Point3D | null => {
  if (!('x' in item) || !('y' in item)) return null;
  const { x, y } = item;
  const z = 'z' in item ? item.z : 0;
  if ([x, y, z].some((coord) => typeof coord !== 'number')) return null;
  return Point3D.fromObject({ x, y, z });
};

/** If item has "basePoint" and "endPoint", both points, return the represented segment, otherwise null. */
const tryParseSegment = (item: any): LineSegment3D | null => {
  if (!('basePoint' in item) || !('endPoint' in item)) return null;
  const basePoint = tryParsePoint(item.basePoint);
  const endPoint = tryParsePoint(item.endPoint);
  if (!basePoint || !endPoint) return null;
  if (basePoint.equals(endPoint)) return null;
  return new LineSegment3D(basePoint, endPoint);
};

const tryParseLine = (item: any): Line3D | null => {
  if (!('direction' in item)) return null;
  const directionAsPoint = tryParsePoint(item.direction);

  const basePoint =
    'pointNearestOrigin' in item
      ? tryParsePoint(item.pointNearestOrigin)
      : 'basePoint' in item
        ? tryParsePoint(item.basePoint)
        : null;

  if (!directionAsPoint || !basePoint) return null;

  const direction = Direction3D.fromObject(directionAsPoint);
  if (!direction) return null;

  return new Line3D(direction, basePoint);
};

/**
 * If item has "vertices", an array of points, return a polygon or a loop of segments if unsuccessful.
 * If not, return null.
 */
const tryParsePolygon = (item: any): Polygon3D | LineSegment3D[] | null => {
  if (!('vertices' in item)) return null;
  const { vertices } = item;
  if (!Array.isArray(vertices)) return null;
  const points = [];
  for (const point of vertices) {
    const next = tryParsePoint(point);
    if (!next) return null;
    points.push(next);
  }

  try {
    return new Polygon3D(points);
  } catch {
    return circularPairwise(points)
      .map(([p1, p2]) => (p1.equals(p2) ? null : new LineSegment3D(p1, p2)))
      .filter(notNullish);
  }
};

function selectTrussEnvelopes(guids: string) {
  const parsedGuids = [...guids.matchAll(/[-0-9a-f]{36}/g)].flat();
  const trussEnvelopes = store.getState().layout.trussEnvelopes;
  const guidsInLayout = parsedGuids.filter((guid) => guid in trussEnvelopes);
  if (!guidsInLayout.length) {
    console.log('No referenced truss envelope in layout');
    return;
  }
  console.log(`Selecting ${guidsInLayout.length} truss envelopes`);
  store.dispatch(selectItemsByGuid(guidsInLayout, 'trussEnvelope'));
}
if (process.env.NODE_ENV !== 'production') (globalThis as unknown as any).selectTrussEnvelopes = selectTrussEnvelopes;
